import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MEAL_TYPE_OPTIONS = [
  { value: 'breakfast', label: 'Desayuno', icon: 'Coffee' },
  { value: 'lunch', label: 'Almuerzo', icon: 'Utensils' },
  { value: 'dinner', label: 'Cena', icon: 'ChefHat' },
  { value: 'snack', label: 'Snack', icon: 'Cookie' },
];

const SaveMealModal = ({ isOpen, onClose, onSave, currentMealType, foods = [] }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mealType, setMealType] = useState(currentMealType?.toLowerCase() || 'snack');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totalCalories = foods?.reduce((sum, f) => sum + (f?.calories || 0), 0);
  const totalProtein = foods?.reduce((sum, f) => sum + parseFloat(f?.protein || 0), 0);
  const totalCarbs = foods?.reduce((sum, f) => sum + parseFloat(f?.carbs || 0), 0);
  const totalFat = foods?.reduce((sum, f) => sum + parseFloat(f?.fat || 0), 0);

  const handleSave = async () => {
    if (!name?.trim()) {
      setError('Por favor ingresa un nombre para la colección');
      return;
    }
    if (foods?.length === 0) {
      setError('No hay alimentos en esta comida para guardar');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({ name: name?.trim(), mealType, description: description?.trim(), foods });
      setName('');
      setDescription('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al guardar la colección');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 bg-primary/10 rounded-lg">
                <Icon name="BookmarkPlus" size={18} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Guardar Comida</h2>
                <p className="text-xs text-muted-foreground">{foods?.length} alimento{foods?.length !== 1 ? 's' : ''} seleccionado{foods?.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted/50 transition-colors">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Nutrition preview */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-muted/30 rounded-xl">
              {[
                { label: 'Cal', value: Math.round(totalCalories), color: 'text-primary' },
                { label: 'Prot', value: `${Math.round(totalProtein)}g`, color: 'text-blue-500' },
                { label: 'Carbs', value: `${Math.round(totalCarbs)}g`, color: 'text-amber-500' },
                { label: 'Grasa', value: `${Math.round(totalFat)}g`, color: 'text-rose-500' },
              ]?.map(stat => (
                <div key={stat?.label} className="text-center">
                  <p className={`text-base font-bold ${stat?.color}`}>{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              ))}
            </div>

            {/* Foods list preview */}
            <div className="max-h-28 overflow-y-auto space-y-1">
              {foods?.map((food, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Icon name="Circle" size={6} className="text-primary shrink-0" />
                  <span className="text-foreground truncate">{food?.food_name || food?.name}</span>
                  <span className="text-muted-foreground ml-auto shrink-0">{food?.calories} cal</span>
                </div>
              ))}
            </div>

            {/* Name input */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Nombre de la colección <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e?.target?.value)}
                placeholder="Ej: Desayuno proteico, Almuerzo ligero..."
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={60}
              />
            </div>

            {/* Meal type selector */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Tipo de comida</label>
              <div className="grid grid-cols-4 gap-2">
                {MEAL_TYPE_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setMealType(opt?.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-all ${
                      mealType === opt?.value
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted/20 text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <Icon name={opt?.icon} size={16} />
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Descripción <span className="text-muted-foreground text-xs">(opcional)</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e?.target?.value)}
                placeholder="Notas sobre esta combinación..."
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={120}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={16} className="text-destructive shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-5 pt-0">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="default"
              className="flex-1"
              onClick={handleSave}
              disabled={saving || !name?.trim()}
              iconName={saving ? undefined : 'BookmarkPlus'}
              iconPosition="left"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SaveMealModal;
